import React, { useMemo } from "react";
import { Text } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";

import { Room } from "@/utils/api";
import { RoomTypeBadge } from "../RoomTypeBadge";
import { TagsBadge } from "../../TagsBadge";

export interface RoomsTableProps {
  /** Array of rooms to display */
  rooms: Room[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditRoom?: (room: Room) => void;

  onDuplicateRoom?: (room: Room) => void;

  /** Callback when remove action is clicked */
  onRemoveRoom?: (room: Room) => void;

  /** Callback when view action is clicked */
  onViewRoom?: (room: Room) => void;

  /** ID of room currently being removed */
  removingRoomId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const RoomsTable: React.FC<RoomsTableProps> = ({
  rooms,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditRoom,
  onDuplicateRoom,
  onRemoveRoom,
  onViewRoom,
  removingRoomId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Room>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Room Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Unnamed Room"}</Text>
            {record.roomNumber && (
              <Text size="xs" c="dimmed">
                Room #{record.roomNumber}
              </Text>
            )}
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {record.description}
              </Text>
            )}
            <TagsBadge
              value={record.categories?.map((category) => category?.name ?? "")}
            />
          </div>
        ),
      },
      {
        key: "roomTypeId",
        label: "Room Type",
        sortable: true,
        width: 200,
        minWidth: 200,
        align: "left",
        render: (value) => {
          if (!value) return "-";
          return <RoomTypeBadge value={value} />;
        },
      },
      {
        key: "capacity",
        label: "Capacity",
        sortable: true,
        width: 150,
        minWidth: 150,
        align: "left",
        render: (value) => {
          if (!value) return "-";
          return (
            <Text size="sm" fw={500}>
              {value} {value === 1 ? "person" : "people"}
            </Text>
          );
        },
      },
      {
        key: "floor",
        label: "Floor",
        sortable: true,
        width: 100,
        minWidth: 100,
        align: "left",
        hideOnMobile: true,
        render: (value) => {
          if (!value) return "-";
          return <Text size="sm">{value}</Text>;
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 200,
        minWidth: 200,
        align: "left",
        render: (_, record) => (
          <RecordActions
            context={record}
            onEditClick={onEditRoom}
            onDuplicateClick={onDuplicateRoom}
            onRemoveClick={onRemoveRoom}
            onGoInClick={undefined} // Rooms don't have sub-items to navigate to
            removeInProgress={removingRoomId === record.id}
          />
        ),
      },
    ],
    [onViewRoom, onEditRoom, onRemoveRoom, removingRoomId]
  );

  return (
    <AppTable
      data={rooms}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(room) => room?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
